import { createRuntimeConfig, debugWarn, getConfigError } from "./config";
import {
  AdConfigError,
  AdInputError,
  requestAd as requestAdFromApi,
  requestAds as requestAdsFromApi,
} from "./requester";
import {
  AdRenderError,
  createVideoAdSession as createRenderedVideoAdSession,
  renderAd as renderAdIntoTarget,
} from "./renderer";
import { collectSignals } from "./signals";
import type {
  AdBatchDecision,
  AdBatchRenderEntry,
  AdBatchRequestOptions,
  AdDecision,
  AdLifecycleEvent,
  AdRequestOptions,
  AdRenderTarget,
  AdSenseConfigOptions,
  AdSenseGlobal,
  AdSenseRequestAndRenderOptions,
  RuntimeConfig,
  VideoAdSession,
} from "./types";

declare global {
  interface Window {
    AdSense?: AdSenseGlobal;
  }
}

let runtimeConfig: RuntimeConfig | undefined;
const AUTO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

type AutoRefreshState = {
  stopped: boolean;
  refreshing: boolean;
  timerId?: ReturnType<typeof setTimeout>;
  renderCleanup?: () => void;
  cleanup?: () => void;
};

const autoRefreshStates = new WeakMap<HTMLElement, AutoRefreshState>();

function emit(
  options: { onEvent?: AdRequestOptions["onEvent"] },
  event: AdLifecycleEvent,
  ad?: AdDecision,
  error?: Error,
): void {
  try {
    options.onEvent?.(event, ad, error);
  } catch (callbackError) {
    console.error("Publisher onEvent handler threw an exception:", callbackError);
  }
}

function configuredRuntime(options: AdSenseConfigOptions = {}): RuntimeConfig {
  const config = createRuntimeConfig(options, runtimeConfig);
  const configError = getConfigError(config);

  if (configError) {
    throw new AdConfigError(configError);
  }

  return config;
}

function configure(options: AdSenseConfigOptions = {}): void {
  const config = createRuntimeConfig(options, runtimeConfig);
  const configError = getConfigError(config);

  if (configError) {
    throw new AdConfigError(configError);
  }

  runtimeConfig = config;
}

function buildRequestSignals(
  config: RuntimeConfig,
  options: Pick<AdRequestOptions, "privacy" | "signals">,
) {
  const privacy = options.privacy ?? config.privacy;

  return collectSignals({
    ...options.signals,
    ...(privacy?.nonPersonalizedAds !== undefined
      ? { non_personalized_ads: privacy.nonPersonalizedAds }
      : {}),
    ...(privacy?.limitedAds !== undefined
      ? { limited_ads: privacy.limitedAds }
      : {}),
  });
}

async function requestAd(
  placementId: string,
  options: AdRequestOptions = {},
): Promise<AdDecision | null> {
  if (typeof placementId !== "string" || !placementId.trim()) {
    throw new AdInputError("placementId is required.");
  }

  const normalizedPlacementId = placementId.trim();

  const config = configuredRuntime({
    baseUrl: options.baseUrl,
    lazyLoad: options.lazyLoad,
    privacy: options.privacy,
    video: options.video,
  });
  const requestSignals = buildRequestSignals(config, options);

  emit(options, "requested");

  try {
    const ad = await requestAdFromApi(config, normalizedPlacementId, requestSignals);
    emit(options, ad ? "response" : "nofill", ad ?? undefined);

    return ad;
  } catch (error) {
    debugWarn(
      config,
      error instanceof Error ? error.message : "Ad request failed.",
    );
    emit(
      options,
      "error",
      undefined,
      error instanceof Error ? error : new Error("Ad request failed."),
    );
    throw error;
  }
}

function resolveLazyTarget(target: AdRenderTarget): HTMLElement {
  if (typeof target !== "string") {
    return target;
  }

  const element = document.querySelector<HTMLElement>(target);

  if (!element) {
    throw new AdInputError(`target not found (${target}).`);
  }

  return element;
}

function resolveOptionalTarget(target: AdRenderTarget): HTMLElement | undefined {
  if (typeof target !== "string") {
    return target;
  }

  if (typeof document === "undefined") {
    return undefined;
  }

  return document.querySelector<HTMLElement>(target) ?? undefined;
}

function clearAdTarget(target: HTMLElement): void {
  const cleanup = (target as unknown as { __adsenseCleanup?: () => void })
    .__adsenseCleanup;
  if (typeof cleanup === "function") {
    cleanup();
    delete (target as unknown as { __adsenseCleanup?: () => void })
      .__adsenseCleanup;
  }

  if (typeof target.replaceChildren === "function") {
    target.replaceChildren();
  }

  target.textContent = "";
}

function clearRenderedAdTarget(target: HTMLElement): void {
  const state = autoRefreshStates.get(target);

  if (typeof state?.renderCleanup === "function") {
    state.renderCleanup();
    state.renderCleanup = undefined;
    const holder = target as unknown as { __adsenseCleanup?: () => void };
    if (holder.__adsenseCleanup === state.cleanup) {
      delete holder.__adsenseCleanup;
    }
  } else {
    const holder = target as unknown as { __adsenseCleanup?: () => void };
    const cleanup = holder.__adsenseCleanup;
    if (cleanup === state?.cleanup) {
      delete holder.__adsenseCleanup;
    } else if (typeof cleanup === "function") {
      cleanup();
    }
  }

  if (typeof target.replaceChildren === "function") {
    target.replaceChildren();
  }

  target.textContent = "";
}

function collapseAdTarget(target: HTMLElement, collapseEmpty = true): void {
  clearAdTarget(target);

  if (typeof target.removeAttribute === "function") {
    target.removeAttribute("data-adsense-slot");
  }

  if (collapseEmpty) {
    target.style.setProperty("display", "none", "important");
  }
}

function collapseRenderedAdTarget(target: HTMLElement, collapseEmpty = true): void {
  clearRenderedAdTarget(target);

  if (typeof target.removeAttribute === "function") {
    target.removeAttribute("data-adsense-slot");
  }

  if (collapseEmpty) {
    target.style.setProperty("display", "none", "important");
  }
}

function restoreAdTarget(target: HTMLElement): void {
  target.style.removeProperty("display");
}

function stopAutoRefresh(target: HTMLElement, state: AutoRefreshState): void {
  if (state.stopped) {
    return;
  }

  state.stopped = true;

  if (state.timerId !== undefined) {
    clearTimeout(state.timerId);
    state.timerId = undefined;
  }

  if (typeof state.renderCleanup === "function") {
    const renderCleanup = state.renderCleanup;
    state.renderCleanup = undefined;
    renderCleanup();
  }

  const holder = target as unknown as { __adsenseCleanup?: () => void };
  if (holder.__adsenseCleanup === state.cleanup) {
    delete holder.__adsenseCleanup;
  }

  autoRefreshStates.delete(target);
}

function isRefreshTargetAvailable(
  target: AdRenderTarget,
  targetElement: HTMLElement,
): boolean {
  if (
    "isConnected" in targetElement &&
    typeof targetElement.isConnected === "boolean" &&
    !targetElement.isConnected
  ) {
    return false;
  }

  return typeof target !== "string" || resolveOptionalTarget(target) === targetElement;
}

function attachAutoRefreshCleanup(
  targetElement: HTMLElement,
  state: AutoRefreshState,
): void {
  const renderCleanup = (targetElement as unknown as {
    __adsenseCleanup?: () => void;
  }).__adsenseCleanup;

  state.renderCleanup =
    typeof renderCleanup === "function" ? renderCleanup : undefined;

  const cleanup = () => {
    stopAutoRefresh(targetElement, state);
  };

  state.cleanup = cleanup;
  (targetElement as unknown as { __adsenseCleanup?: () => void })
    .__adsenseCleanup = cleanup;
}

function waitForVisibleTarget(target: AdRenderTarget): Promise<void> {
  if (
    typeof document === "undefined" ||
    typeof IntersectionObserver === "undefined"
  ) {
    return Promise.resolve();
  }

  const element = resolveLazyTarget(target);

  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      observer.disconnect();
      resolve();
    });

    observer.observe(element);
  });
}

function getSdkErrorCode(error: unknown): string | undefined {
  return typeof error === "object" && error !== null && "code" in error
    ? String((error as { code?: unknown }).code)
    : undefined;
}

async function requestAndRenderAd(
  placementId: string,
  target: AdRenderTarget,
  options: AdSenseRequestAndRenderOptions = {},
): Promise<AdDecision | null> {
  const ad = await requestAndRenderAdOnce(placementId, target, options, {
    preserveAutoRefresh: false,
  });

  const targetElement = resolveOptionalTarget(target);
  if (ad && targetElement) {
    startAutoRefresh(placementId, target, targetElement, options);
  }

  return ad;
}

async function requestAndRenderAdOnce(
  placementId: string,
  target: AdRenderTarget,
  options: AdSenseRequestAndRenderOptions,
  refreshOptions: { preserveAutoRefresh: boolean },
): Promise<AdDecision | null> {
  if (typeof placementId !== "string" || !placementId.trim()) {
    throw new AdInputError("placementId is required.");
  }

  const config = configuredRuntime({
    baseUrl: options.baseUrl,
    lazyLoad: options.lazyLoad,
    privacy: options.privacy,
    video: options.video,
  });
  const targetElement = resolveOptionalTarget(target);
  const collapseEmpty = options.collapseEmpty ?? true;

  if (targetElement) {
    if (refreshOptions.preserveAutoRefresh) {
      clearRenderedAdTarget(targetElement);
    } else {
      clearAdTarget(targetElement);
    }
  }

  if (options.lazyLoad ?? config.lazyLoad) {
    await waitForVisibleTarget(target);
  }

  let ad: AdDecision | null;

  try {
    ad = await requestAd(placementId, options);
  } catch (error) {
    if (targetElement) {
      if (refreshOptions.preserveAutoRefresh) {
        collapseRenderedAdTarget(targetElement, collapseEmpty);
      } else {
        collapseAdTarget(targetElement, collapseEmpty);
      }
    }

    throw error;
  }

  if (!ad) {
    if (targetElement) {
      if (refreshOptions.preserveAutoRefresh) {
        collapseRenderedAdTarget(targetElement, collapseEmpty);
      } else {
        collapseAdTarget(targetElement, collapseEmpty);
      }
    }

    return null;
  }

  try {
    if (targetElement) {
      restoreAdTarget(targetElement);
    }

    return await renderAdIntoTarget(target, ad, {
      ...options,
      video: {
        ...config.video,
        ...options.video,
      },
    });
  } catch (error) {
    if (targetElement) {
      if (refreshOptions.preserveAutoRefresh) {
        collapseRenderedAdTarget(targetElement, collapseEmpty);
      } else {
        collapseAdTarget(targetElement, collapseEmpty);
      }
    }

    emit(
      options,
      "error",
      ad,
      error instanceof Error ? error : new Error("Ad render failed."),
    );
    throw error;
  }
}

function scheduleAutoRefresh(
  placementId: string,
  target: AdRenderTarget,
  targetElement: HTMLElement,
  options: AdSenseRequestAndRenderOptions,
  state: AutoRefreshState,
): void {
  if (state.stopped) {
    return;
  }

  state.timerId = setTimeout(() => {
    state.timerId = undefined;

    if (state.stopped) {
      return;
    }

    if (!isRefreshTargetAvailable(target, targetElement)) {
      stopAutoRefresh(targetElement, state);
      return;
    }

    if (state.refreshing) {
      scheduleAutoRefresh(placementId, target, targetElement, options, state);
      return;
    }

    state.refreshing = true;

    void requestAndRenderAdOnce(placementId, target, options, {
      preserveAutoRefresh: true,
    })
      .then((ad) => {
        if (ad && !state.stopped) {
          attachAutoRefreshCleanup(targetElement, state);
        }
      })
      .catch(() => {
        // Request and render paths already emit lifecycle errors and debug warnings.
      })
      .finally(() => {
        state.refreshing = false;

        if (!state.stopped && isRefreshTargetAvailable(target, targetElement)) {
          if (
            (targetElement as unknown as { __adsenseCleanup?: () => void })
              .__adsenseCleanup !== state.cleanup
          ) {
            attachAutoRefreshCleanup(targetElement, state);
          }
          scheduleAutoRefresh(placementId, target, targetElement, options, state);
        } else {
          stopAutoRefresh(targetElement, state);
        }
      });
  }, AUTO_REFRESH_INTERVAL_MS);

  if (
    typeof state.timerId === "object" &&
    state.timerId !== null &&
    "unref" in state.timerId &&
    typeof state.timerId.unref === "function"
  ) {
    state.timerId.unref();
  }
}

function startAutoRefresh(
  placementId: string,
  target: AdRenderTarget,
  targetElement: HTMLElement,
  options: AdSenseRequestAndRenderOptions,
): void {
  const state: AutoRefreshState = {
    stopped: false,
    refreshing: false,
  };

  autoRefreshStates.set(targetElement, state);
  attachAutoRefreshCleanup(targetElement, state);
  scheduleAutoRefresh(placementId, target, targetElement, options, state);
}

async function requestAndRenderAdSafe(
  placementId: string,
  target: AdRenderTarget,
  options: AdSenseRequestAndRenderOptions = {},
): Promise<AdDecision | null> {
  const config = configuredRuntime({
    baseUrl: options.baseUrl,
    lazyLoad: options.lazyLoad,
    privacy: options.privacy,
    video: options.video,
  });

  try {
    return await requestAndRenderAd(placementId, target, options);
  } catch (error) {
    const code = getSdkErrorCode(error);

    if (code === "INPUT_ERROR" || code === "CONFIG_ERROR") {
      throw error;
    }

    debugWarn(
      config,
      error instanceof Error ? error.message : "Ad failed to load.",
    );

    return null;
  }
}

type NormalizedBatchRenderEntry = Omit<AdBatchRenderEntry, "placementId"> & {
  placementId: string;
};

function normalizeBatchRenderEntries(
  entries: AdBatchRenderEntry[],
): NormalizedBatchRenderEntry[] {
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new AdInputError("entries must contain at least one placement.");
  }

  const targets = new Set<AdRenderTarget>();

  return entries.map((entry) => {
    if (!entry || typeof entry !== "object") {
      throw new AdInputError("Each batch entry is required.");
    }

    if (typeof entry.placementId !== "string" || !entry.placementId.trim()) {
      throw new AdInputError("Each batch entry placementId is required.");
    }

    if (
      (typeof entry.target !== "string" &&
        (!entry.target || typeof entry.target !== "object")) ||
      (typeof entry.target === "string" && !entry.target.trim())
    ) {
      throw new AdInputError("Each batch entry target is required.");
    }

    const resolvedTarget = resolveOptionalTarget(entry.target);
    const targetKey = resolvedTarget ?? entry.target;

    if (targets.has(targetKey)) {
      throw new AdInputError("Batch entries must use unique render targets.");
    }

    targets.add(targetKey);

    return {
      ...entry,
      placementId: entry.placementId.trim(),
    };
  });
}

async function requestAndRenderAdsInternal(
  entries: AdBatchRenderEntry[],
  options: AdBatchRequestOptions,
  safe: boolean,
): Promise<AdBatchDecision[]> {
  const normalizedEntries = normalizeBatchRenderEntries(entries);
  const config = configuredRuntime({
    baseUrl: options.baseUrl,
    lazyLoad: options.lazyLoad,
    privacy: options.privacy,
  });
  const targetElements = normalizedEntries.map((entry) =>
    resolveOptionalTarget(entry.target),
  );

  for (const targetElement of targetElements) {
    if (targetElement) {
      clearAdTarget(targetElement);
    }
  }

  if (options.lazyLoad ?? config.lazyLoad) {
    await Promise.all(
      normalizedEntries.map((entry) => waitForVisibleTarget(entry.target)),
    );
  }

  for (const entry of normalizedEntries) {
    emit(entry.options ?? {}, "requested");
  }

  let decisions: AdBatchDecision[];

  try {
    decisions = await requestAdsFromApi(
      config,
      normalizedEntries.map((entry) => entry.placementId),
      buildRequestSignals(config, options),
    );
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error("Batch ad request failed.");

    debugWarn(config, normalizedError.message);
    normalizedEntries.forEach((entry, index) => {
      const targetElement = targetElements[index];
      if (targetElement) {
        collapseAdTarget(
          targetElement,
          entry.options?.collapseEmpty ?? true,
        );
      }
      emit(entry.options ?? {}, "error", undefined, normalizedError);
    });

    if (!safe || getSdkErrorCode(error) === "INPUT_ERROR" || getSdkErrorCode(error) === "CONFIG_ERROR") {
      throw error;
    }

    return normalizedEntries.map((entry) => ({
      placementId: entry.placementId,
      ad: null,
    }));
  }

  return Promise.all(
    decisions.map(async (decision, index) => {
      const entry = normalizedEntries[index];
      const targetElement = targetElements[index];
      const entryOptions = entry.options ?? {};

      emit(
        entryOptions,
        decision.ad ? "response" : "nofill",
        decision.ad ?? undefined,
      );

      if (!decision.ad) {
        if (targetElement) {
          collapseAdTarget(targetElement, entryOptions.collapseEmpty ?? true);
        }

        return decision;
      }

      try {
        if (targetElement) {
          restoreAdTarget(targetElement);
        }

        const renderedAd = await renderAdIntoTarget(entry.target, decision.ad, {
          ...entryOptions,
          video: {
            ...config.video,
            ...entryOptions.video,
          },
        });

        if (targetElement) {
          startAutoRefresh(
            entry.placementId,
            entry.target,
            targetElement,
            { ...options, ...entryOptions },
          );
        }

        return {
          placementId: decision.placementId,
          ad: renderedAd,
        };
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Ad render failed.");

        if (targetElement) {
          collapseAdTarget(targetElement, entryOptions.collapseEmpty ?? true);
        }
        emit(entryOptions, "error", decision.ad, normalizedError);

        if (!safe) {
          throw error;
        }

        debugWarn(config, normalizedError.message);
        return {
          placementId: decision.placementId,
          ad: null,
        };
      }
    }),
  );
}

async function requestAndRenderAds(
  entries: AdBatchRenderEntry[],
  options: AdBatchRequestOptions = {},
): Promise<AdBatchDecision[]> {
  return requestAndRenderAdsInternal(entries, options, false);
}

async function requestAndRenderAdsSafe(
  entries: AdBatchRenderEntry[],
  options: AdBatchRequestOptions = {},
): Promise<AdBatchDecision[]> {
  return requestAndRenderAdsInternal(entries, options, true);
}

async function createVideoAdSession(
  placementId: string,
  target: AdRenderTarget,
  options: AdSenseRequestAndRenderOptions = {},
): Promise<VideoAdSession | null> {
  if (typeof placementId !== "string" || !placementId.trim()) {
    throw new AdInputError("placementId is required.");
  }

  if (options.video?.variant !== "preroll" || options.video?.mode !== "headless") {
    throw new AdInputError(
      "createVideoAdSession requires video.variant to be preroll and video.mode to be headless.",
    );
  }

  const config = configuredRuntime({
    baseUrl: options.baseUrl,
    lazyLoad: options.lazyLoad,
    privacy: options.privacy,
    video: options.video,
  });
  const targetElement = resolveOptionalTarget(target);
  const collapseEmpty = options.collapseEmpty ?? true;

  if (targetElement) {
    clearAdTarget(targetElement);
  }

  if (options.lazyLoad ?? config.lazyLoad) {
    await waitForVisibleTarget(target);
  }

  let ad: AdDecision | null;

  try {
    ad = await requestAd(placementId, options);
  } catch (error) {
    if (targetElement) {
      collapseAdTarget(targetElement, collapseEmpty);
    }

    throw error;
  }

  if (!ad) {
    if (targetElement) {
      collapseAdTarget(targetElement, collapseEmpty);
    }

    return null;
  }

  if (ad.format !== "video") {
    if (targetElement) {
      collapseAdTarget(targetElement, collapseEmpty);
    }

    const error = new AdRenderError(
      "Cannot create video ad session: ad response format must be video.",
    );
    emit(options, "error", ad, error);
    throw error;
  }

  try {
    if (targetElement) {
      restoreAdTarget(targetElement);
    }

    return await createRenderedVideoAdSession(target, ad, {
      ...options,
      video: {
        ...config.video,
        ...options.video,
        variant: "preroll",
        mode: "headless",
      },
    });
  } catch (error) {
    if (targetElement) {
      collapseAdTarget(targetElement, collapseEmpty);
    }

    emit(
      options,
      "error",
      ad,
      error instanceof Error ? error : new Error("Video ad session failed."),
    );
    throw error;
  }
}

const AdSense: AdSenseGlobal = {
  configure,
  requestAndRenderAd,
  requestAndRenderAdSafe,
  requestAndRenderAds,
  requestAndRenderAdsSafe,
  createVideoAdSession,
};

if (typeof window !== "undefined") {
  window.AdSense = AdSense;
}
